const defaultConfig = {
  id: null,
  product: "ai-chat",
  settings: {
    serviceType: "Article Composer",
    language: "English (British English)",
    articleTone: "Informative",
    writingStyle: "",
  },
  options: {
    frequency_penalty: 0.2,
    max_length: null,
    messages: [],
    model: "latest",
    presence_penalty: 0.2,
    response_format: { type: "text" },
    temperature: 0.5,
    n: 1,
    user: null,
    tool_choice: "auto",
    suggestionsEnabled: false,
  },
  user_id: null,
  user_name: null,
};

const isObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const deepMergeSafe = (...objs) => {
  const result = {};
  for (const obj of objs) {
    if (!isObject(obj)) continue;
    for (const key of Object.keys(obj)) {
      const prev = result[key];
      const next = obj[key];

      if (Array.isArray(next)) {
        // arrays: last-one-wins (shallow copy)
        result[key] = next.slice();
      } else if (isObject(next)) {
        result[key] = isObject(prev) ? deepMergeSafe(prev, next) : deepMergeSafe(next);
      } else {
        result[key] = next;
      }
    }
  }
  return result;
};

const validateAndFormatTemperature = (temperature) => {
  const n = parseFloat(temperature);
  if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
  console.warn("Temperature value is invalid, setting to default 0.5");
  return 0.5;
};

// validate temperature in-place for a config-like shape (options.temperature)
const normalizeTemperatureOn = (obj) => {
  if (isObject(obj) && isObject(obj.options) && "temperature" in obj.options) {
    obj.options = { ...obj.options, temperature: validateAndFormatTemperature(obj.options.temperature) };
  }
  return obj;
};

// tool_choice: override > saved > default; must be string OR object with 'function'
const resolveToolChoice = (defChoice, saved, overrides) => {
  const ok = (choice) =>
    typeof choice === "string" || (isObject(choice) && "function" in choice);

  if (ok(overrides?.options?.tool_choice)) return overrides.options.tool_choice;
  if (ok(saved?.options?.tool_choice)) return saved.options.tool_choice;
  return defChoice;
};

export const generateConfig = (configOverrides = {}, enableMergedSettings = true) => {
  // Load saved settings; tolerate malformed JSON
  let savedSettings = {};
  try {
    savedSettings = JSON.parse(localStorage.getItem("settings")) || {};
  } catch {
    savedSettings = {};
  }

  // Extract model from saved or default (keeps your original precedence)
  const modelIdFromSavedSettings =
    savedSettings.modelId || defaultConfig.options.model;

  // Prevent leaking modelId into DB
  if ("modelId" in savedSettings) {
    const { modelId, ...rest } = savedSettings;
    savedSettings = rest;
  }

  // Ensure temperatures are normalized where they actually live (options.temperature)
  normalizeTemperatureOn({ options: savedSettings.options || {} });
  normalizeTemperatureOn(configOverrides);

  // Compute tool_choice per your precedence
  const toolChoice = resolveToolChoice(
    defaultConfig.options.tool_choice,
    savedSettings,
    configOverrides
  );

  // Merge blocks with explicit precedence.
  // Settings block: overrides > saved > default
  const mergedSettings = enableMergedSettings
    ? deepMergeSafe(defaultConfig.settings, savedSettings.settings, configOverrides.settings)
    : deepMergeSafe(defaultConfig.settings, configOverrides.settings); // skip saved if disabled

  // Options block: overrides > saved > default (except model & tool_choice handled later)
  const mergedOptions = deepMergeSafe(
    defaultConfig.options,
    savedSettings.options,
    configOverrides.options
  );

  // Whole-config merge for any top-level extras in overrides/saved (id, user fields, etc.)
  const mergedTopLevel = deepMergeSafe(
    defaultConfig,
    // only include known top-level keys from saved to avoid pollution
    {
      id: savedSettings.id,
      user_id: savedSettings.user_id,
      user_name: savedSettings.user_name,
      product: savedSettings.product,
    },
    configOverrides
  );

  // Assemble the final config
  const finalConfig = {
    ...mergedTopLevel,
    settings: mergedSettings,
    options: {
      ...mergedOptions,
      tool_choice: toolChoice,
      // MODEL precedence: keep your original behavior (saved OR default).
      // If you want overrides to win, change to:
      // model: configOverrides?.options?.model ?? modelIdFromSavedSettings
      model: modelIdFromSavedSettings,
    },
  };

  // Final sanity: ensure temperature exists and valid
  finalConfig.options.temperature = validateAndFormatTemperature(
    finalConfig.options.temperature ?? defaultConfig.options.temperature
  );

  return finalConfig;
};
